import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  TextField,
  MenuItem,
  Paper,
  Typography,
  Alert,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Grid,
  FormControlLabel,
  Checkbox,
  useMediaQuery,
  Stack,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  InputAdornment,
  Divider,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { 
  createProduct, 
  updateProduct, 
  getProduct, 
  getSKUs, 
  updateSKU, 
  deleteProduct,
  getProductAttributes,
  createProductAttribute,
  updateProductAttribute,
  getAffectedSKUsByAttribute,
  deleteProductAttribute,
  createProductAttributeOption,
  getAffectedSKUsByOption,
  deleteProductAttributeOption,
  listSupplyPurchases,
  addSupplyPurchase,
  deleteSupplyPurchase,
  setVariationCombinationSelections,
} from '../app/api';
import { useTranslation } from '../app/LanguageContext';
import ConfirmDialog from '../components/ConfirmDialog';
import MediaGallery from '../components/MediaGallery';
import FieldHelpIcon from '../components/FieldHelpIcon';
import { SavingOverlay } from '../components/SavingOverlay';
import { useBusyCount } from '../hooks/useBusyCount';
import { applyCurrencyMask, parseCurrency, formatCurrency } from '../app/currency';
import { ExtensionSlot } from '../components/ExtensionSlot';

const customizableTypeLabels = { text: 'Texto', number: 'Número', date: 'Data', textarea: 'Texto longo', select: 'Seleção', image: 'Imagem' };

function canonicalCombinationKeyFromMap(attrsMap) {
  const keys = Object.keys(attrsMap || {}).sort();
  return keys.map((k) => `${k}:${String(attrsMap[k] ?? '').trim()}`).join('|');
}

function attributeOptionStrings(attr) {
  const opts = attr.options || [];
  const out = [];
  opts.forEach((opt) => {
    const v = typeof opt === 'string' ? opt : String(opt?.value ?? opt?.label ?? '');
    const trimmed = String(v ?? '').trim();
    if (trimmed) out.push(trimmed);
  });
  return out;
}

function buildEmptyVariationIncludedMaps(dims) {
  const next = {};
  dims.forEach((attr) => {
    const ak = attr.key || attr.attribute_key;
    next[ak] = {};
    attributeOptionStrings(attr).forEach((v) => {
      next[ak][v] = false;
    });
  });
  return next;
}

function computeCandidateCombinationRows(fullCartesianRows, dims, includedByAttr) {
  if (!fullCartesianRows.length || dims.length === 0) return [];
  return fullCartesianRows.filter((row) =>
    dims.every((attr) => {
      const ak = attr.key || attr.attribute_key;
      const raw = row[ak];
      if (raw === undefined || raw === null || String(raw).trim() === '') return false;
      const val = String(raw).trim();
      return !!includedByAttr[ak]?.[val];
    }),
  );
}

function dimensionSelectionsComplete(dims, includedByAttr) {
  return dims.every((attr) => {
    const ak = attr.key || attr.attribute_key;
    const rec = includedByAttr[ak] || {};
    return attributeOptionStrings(attr).some((v) => rec[v]);
  });
}

const SortableAttributeItem = ({
  attribute,
  onDelete,
  onAddOption,
  editingOptionAttributeId,
  setEditingOptionAttributeId,
  handleDeleteOption,
  onDragEndOptions,
  optionSensors,
  isEditing,
  onEdit,
  onSaveEdit,
  onCancelEdit,
  onAttributeChange,
  onDefaultOptionChange,
}) => {
  const { translate } = useTranslation();
  const [optionDraft, setOptionDraft] = useState('');
  const optionInputRef = useRef(null);

  useEffect(() => {
    if (editingOptionAttributeId !== attribute.id) {
      setOptionDraft('');
    }
  }, [editingOptionAttributeId, attribute.id]);

  const commitExistingAttributeOption = () => {
    const trimmed = optionDraft.trim();
    if (!trimmed) {
      return;
    }
    onAddOption(attribute.id, trimmed);
    setOptionDraft('');
    requestAnimationFrame(() => {
      optionInputRef.current?.focus?.();
    });
  };

  const handleExistingOptionKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      commitExistingAttributeOption();
    }
  };

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: attribute.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Paper ref={setNodeRef} style={style} sx={{ p: 2, mb: 2, cursor: 'grab', '&:active': { cursor: 'grabbing' } }}>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} md={1}>
          <IconButton
            {...attributes}
            {...listeners}
            size="small"
            sx={{ cursor: 'grab', '&:active': { cursor: 'grabbing' } }}
          >
            <DragIndicatorIcon />
          </IconButton>
        </Grid>
        <Grid item xs={12} md={2}>
          <Typography variant="subtitle1" fontWeight="bold">
            {attribute.name || attribute.key}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mt: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
            {attribute.is_required && <Chip label={translate('products.attribute_required')} size="small" color="primary" />}
            {attribute.is_variation && <Chip label={translate('products.attribute_variation')} size="small" />}
            {attribute.is_customizable && (
              <Chip
                label={`${translate('products.attribute_customizable')}${attribute.customizable_type ? ` (${customizableTypeLabels[attribute.customizable_type] || attribute.customizable_type})` : ''}`}
                size="small"
              />
            )}
            {attribute.is_customizable && attribute.customizable_type === 'select' && !isEditing && (
              <Button size="small" variant="outlined" onClick={onEdit}>
                Editar
              </Button>
            )}
            {isEditing && attribute.customizable_type === 'select' && (
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={attribute.allow_other || false}
                      onChange={(e) => onAttributeChange(attribute.id, 'allow_other', e.target.checked)}
                    />
                  }
                  label={translate('products.attribute_allow_other')}
                />
                <Button size="small" variant="contained" onClick={() => onSaveEdit(attribute.id)}>
                  Salvar
                </Button>
                <Button size="small" onClick={onCancelEdit}>
                  Cancelar
                </Button>
              </Box>
            )}
          </Box>
        </Grid>
        <Grid item xs={12} md={6}>
          <Typography variant="body2" color="text.secondary" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
            {translate('products.attribute_options')}:
            <FieldHelpIcon title={translate('products.attribute_options_help')} />
          </Typography>
          {attribute.is_customizable && attribute.customizable_type !== 'select' ? (
            <Typography variant="body2" color="text.secondary">
              Preenchido pelo cliente no orçamento
            </Typography>
          ) : (attribute.is_variation || (attribute.is_customizable && attribute.customizable_type === 'select')) && attribute.options && attribute.options.length > 0 ? (
            <DndContext
              sensors={optionSensors}
              collisionDetection={closestCenter}
              onDragEnd={(event) => onDragEndOptions(event, attribute.id)}
            >
              <SortableContext
                items={attribute.options.map(opt => opt.id)}
                strategy={verticalListSortingStrategy}
              >
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                  {attribute.options.map((opt) => (
                    <SortableOptionItem
                      key={opt.id}
                      option={opt}
                      onDelete={() => handleDeleteOption(attribute.id, opt.id)}
                    />
                  ))}
                </Box>
              </SortableContext>
            </DndContext>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Nenhuma opção definida
            </Typography>
          )}
          {((attribute.is_variation || (attribute.is_customizable && attribute.customizable_type === 'select')) && editingOptionAttributeId === attribute.id) ? (
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1}
              sx={{ alignItems: { xs: 'stretch', sm: 'center' }, flexWrap: 'wrap', mt: 0.5 }}
            >
              <TextField
                inputRef={optionInputRef}
                size="small"
                placeholder="Nova opção"
                value={optionDraft}
                onChange={(e) => setOptionDraft(e.target.value)}
                onKeyDown={handleExistingOptionKeyDown}
                autoFocus
                fullWidth
                sx={{ flex: { sm: '1 1 140px' }, minWidth: { sm: 140 } }}
                inputProps={{ 'data-testid': 'attribute-existing-option-input' }}
              />
              <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
                <Button
                  size="small"
                  variant="contained"
                  disabled={!optionDraft.trim()}
                  onClick={commitExistingAttributeOption}
                >
                  {translate('common.add')}
                </Button>
                <Button
                  size="small"
                  onClick={() => setEditingOptionAttributeId(null)}
                >
                  Cancelar
                </Button>
              </Stack>
            </Stack>
          ) : (attribute.is_variation || (attribute.is_customizable && attribute.customizable_type === 'select')) ? (
            <Button
              size="small"
              variant="outlined"
              onClick={() => setEditingOptionAttributeId(attribute.id)}
              data-testid="attribute-add-option"
            >
              {translate('products.attribute_add_option')}
            </Button>
          ) : null}
          {(attribute.is_variation || (attribute.is_required && !attribute.is_customizable)) &&
            attribute.options &&
            attribute.options.length > 0 && (
            <Box sx={{ mt: 1.5, width: '100%' }}>
              <TextField
                select
                size="small"
                fullWidth
                label={translate('products.attribute_default_option')}
                value={attribute.default_option_id || ''}
                onChange={(e) => onDefaultOptionChange?.(attribute.id, e.target.value || null)}
                helperText={translate('products.attribute_default_option_help')}
              >
                <MenuItem value="">
                  <em>—</em>
                </MenuItem>
                {attribute.options.map((opt) => (
                  <MenuItem key={opt.id} value={opt.id}>
                    {opt.value}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
          )}
        </Grid>
        <Grid item xs={12} md={3}>
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <Button
              size="small"
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() => onDelete(attribute.id)}
            >
              Excluir
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Paper>
  );
};

// Sortable Option Item Component
const SortableOptionItem = ({ option, onDelete }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: option.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Box ref={setNodeRef} style={style} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
      <Chip
        label={option.value}
        size="small"
        icon={<DragIndicatorIcon sx={{ fontSize: 16 }} />}
        sx={{ cursor: 'grab', '&:active': { cursor: 'grabbing' } }}
        {...attributes}
        {...listeners}
      />
      <IconButton
        size="small"
        onClick={onDelete}
        aria-label={`Excluir opção ${option.value}`}
        sx={{ p: 0.25 }}
      >
        <DeleteIcon fontSize="small" />
      </IconButton>
    </Box>
  );
};

const ProductFormPage = () => {
  const theme = useTheme();
  const isXsBreakpoint = useMediaQuery(theme.breakpoints.down('sm'));
  const { translate } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;

  const [formData, setFormData] = useState({
    code: '',
    name: '',
    description: '',
    status: 'ACTIVE',
    type: 'PRODUCT',
  });
  const [skus, setSkus] = useState([]);
  const [savingSkuIds, setSavingSkuIds] = useState(new Set());
  const [errors, setErrors] = useState({});
  const [skuErrors, setSkuErrors] = useState({});
  const [supplyDialogSkuId, setSupplyDialogSkuId] = useState(null);
  const [supplyPurchasesList, setSupplyPurchasesList] = useState([]);
  const [supplyPurchasesLoading, setSupplyPurchasesLoading] = useState(false);
  const [supplyForm, setSupplyForm] = useState({
    quantity: '',
    unitCost: '',
    notes: '',
    purchasedAt: '',
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [loading, setLoading] = useState(false);
  const { busy: pageBusy, begin: beginPageBusy, end: endPageBusy } = useBusyCount();
  const blockingOverlayOpen = loading || pageBusy;
  const [loadingData, setLoadingData] = useState(true);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, type: null, payload: null });
  const [productId, setProductId] = useState(null);
  const [productAttributes, setProductAttributes] = useState([]);
  const [editingAttributeId, setEditingAttributeId] = useState(null);
  const [newAttribute, setNewAttribute] = useState(null);
  const [newAttributeOptionDraft, setNewAttributeOptionDraft] = useState('');
  const [editingOptionAttributeId, setEditingOptionAttributeId] = useState(null);
  const newAttributeOptionInputRef = useRef(null);
  const confirmDialogRef = useRef(confirmDialog);
  confirmDialogRef.current = confirmDialog;
  const pendingVariationAttributeSaveRef = useRef(null);
  const previousAttributeValuesRef = useRef({});
  const confirmDialogActionLockRef = useRef(false);
  const [skuCodeFilter, setSkuCodeFilter] = useState('');
  const [skuAttrFilters, setSkuAttrFilters] = useState({});
  const [variationComboOpen, setVariationComboOpen] = useState(false);
  const [variationComboDimIncluded, setVariationComboDimIncluded] = useState({});

  const loadData = useCallback(async (showLoading = true) => {
    try {
      if (showLoading) setLoadingData(true);
      const [productResponse, skusResponse, attributesResponse] = await Promise.all([
        getProduct(id),
        getSKUs(id),
        getProductAttributes(id).catch(() => []),
      ]);
      if (productResponse.data) {
        setFormData({
          code: productResponse.data.code || '',
          name: productResponse.data.name,
          description: productResponse.data.description,
          status: productResponse.data.status,
          type: productResponse.data.type || 'PRODUCT',
        });
        setProductId(productResponse.data.id);
      }
      setSkus(skusResponse.data || []);
      // Python API: { data: { attributes: [...] } }; getProductAttributes normalizes to data: []
      const rawAttrs = Array.isArray(attributesResponse)
        ? attributesResponse
        : Array.isArray(attributesResponse?.data)
          ? attributesResponse.data
          : attributesResponse?.data?.attributes ?? [];
      const attributes = Array.isArray(rawAttrs) ? rawAttrs : [];
      // Sort attributes by display_order
      const sortedAttributes = [...attributes].sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
      // Sort options within each attribute
      sortedAttributes.forEach(attr => {
        if (attr.options && attr.options.length > 0) {
          attr.options.sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
        }
      });
      setProductAttributes(sortedAttributes);
      previousAttributeValuesRef.current = Object.fromEntries(
        sortedAttributes.map((a) => [a.id, { is_variation: !!a.is_variation, is_required: !!a.is_required }]),
      );
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message,
        severity: 'error',
      });
    } finally {
      setLoadingData(false);
    }
  }, [id]);

  useEffect(() => {
    if (isEdit) {
      loadData();
    } else {
      setLoadingData(false);
    }
  }, [isEdit, loadData]);

  useEffect(() => {
    if (!newAttribute) {
      setNewAttributeOptionDraft('');
    }
  }, [newAttribute]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const validate = (dataToValidate = formData) => {
    const newErrors = {};

    if (!dataToValidate.name || dataToValidate.name.trim() === '') {
      newErrors.name = translate('products.name_required');
    }

    if (!dataToValidate.status) {
      newErrors.status = translate('products.status_required');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate(formData)) {
      return;
    }

    setLoading(true);

    try {
      const productData = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        status: formData.status,
        type: formData.type || 'PRODUCT',
      };

      if (isEdit) {
        await updateProduct(id, productData);
        await loadData(false);
        setSnackbar({
          open: true,
          message: translate('products.updated'),
          severity: 'success',
        });
        navigate(`/products/${formData.code || id}/edit`);
        return;
      }

      const response = await createProduct(productData);
      setSnackbar({
        open: true,
        message: translate('products.created'),
        severity: 'success',
      });
      navigate(`/products/${response.data.code || response.data.id}/edit`);
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message,
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const productKeyForApis = productId || id;

  const variationAttributes = useMemo(
    () => productAttributes.filter((a) => a.is_variation),
    [productAttributes],
  );

  const nominalVariationDimsForSku = useMemo(() => {
    const byVar = productAttributes.filter((a) => a.is_variation);
    let dims = byVar.filter((a) => a.is_required);
    if (dims.length === 0) {
      dims = byVar.slice();
    }
    return dims.filter((a) => Array.isArray(a.options) && a.options.length > 0);
  }, [productAttributes]);

  const skuCombinationCartesianRows = useMemo(() => {
    if (nominalVariationDimsForSku.length === 0) {
      return [];
    }
    let rows = [{}];
    nominalVariationDimsForSku.forEach((attr) => {
      const attrKey = attr.key || attr.attribute_key;
      const nextRows = [];
      const opts = attr.options || [];
      rows.forEach((base) => {
        opts.forEach((opt) => {
          const v = typeof opt === 'string' ? opt : String(opt?.value ?? opt?.label ?? '');
          const trimmed = v.trim();
          if (!trimmed) {
            return;
          }
          nextRows.push({ ...base, [attrKey]: trimmed });
        });
      });
      rows = nextRows.length > 0 ? nextRows : rows;
    });
    return rows;
  }, [nominalVariationDimsForSku]);

  const variationModalCandidateRows = useMemo(
    () => computeCandidateCombinationRows(skuCombinationCartesianRows, nominalVariationDimsForSku, variationComboDimIncluded),
    [skuCombinationCartesianRows, nominalVariationDimsForSku, variationComboDimIncluded],
  );

  const skuTableColumnCount = 4 + variationAttributes.length + 2;

  const skuTablePriceColWidthPx = 124;
  const skuTableToggleColWidthPx = 84;
  const skuTableMtoColWidthPx = 92;
  const skuTableValueColWidthPx = 168;
  const skuValueRowBoxPx = 140;
  const skuStockInputPx = 104;
  const skuPrazoInputPx = 140;
  const skuGridInputSx = {
    '& .MuiInputBase-root': { height: 40 },
    '& .MuiInputBase-input': { py: 1, fontSize: '0.875rem' },
    '& .MuiFormHelperText-root': { mx: 0, mt: 0.5, position: 'static' },
  };

  const getSkuAttributeValueForKey = (attributes, attributeKey) => {
    if (!attributes || attributeKey === undefined || attributeKey === null || attributeKey === '') {
      return undefined;
    }
    if (typeof attributes === 'object' && !Array.isArray(attributes)) {
      const v = attributes[attributeKey];
      if (v === undefined || v === null) return undefined;
      return String(v);
    }
    if (Array.isArray(attributes)) {
      const found = attributes.find(
        (a) => (a.key || a.attribute_key) === attributeKey
      );
      if (!found) return undefined;
      const val = found.value ?? found.attribute_value;
      if (val === undefined || val === null) return undefined;
      return String(val);
    }
    return undefined;
  };

  const variationAttributesKey = useMemo(
    () =>
      variationAttributes.map((attr) => `${attr.id}:${attr.key || attr.attribute_key || ''}`).join('|'),
    [variationAttributes],
  );

  useEffect(() => {
    setSkuAttrFilters({});
  }, [variationAttributesKey]);

  const skuAttrFilterOptions = useMemo(() => {
    const opts = {};
    variationAttributes.forEach((attr) => {
      const key = attr.key || attr.attribute_key;
      const uniq = new Set();
      skus.forEach((sku) => {
        const raw = getSkuAttributeValueForKey(sku.attributes, key);
        if (raw !== undefined && raw !== '') {
          uniq.add(raw);
        }
      });
      opts[key] = Array.from(uniq).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
    });
    return opts;
  }, [skus, variationAttributes]);

  const filteredSkus = useMemo(() => {
    const codeNeedle = skuCodeFilter.trim().toLowerCase();
    return skus.filter((sku) => {
      if (codeNeedle !== '') {
        const codeHay = ((sku.code ?? '') || '').toString().toLowerCase();
        if (!codeHay.includes(codeNeedle)) {
          return false;
        }
      }
      const okAttrs = variationAttributes.every((attr) => {
        const key = attr.key || attr.attribute_key;
        const selected = skuAttrFilters[key];
        if (!selected) {
          return true;
        }
        const cell = getSkuAttributeValueForKey(sku.attributes, key);
        return cell === selected;
      });
      return okAttrs;
    });
  }, [skus, skuCodeFilter, skuAttrFilters, variationAttributes]);

  const skuFiltersActive =
    skuCodeFilter.trim() !== '' ||
    variationAttributes.some((attr) => {
      const key = attr.key || attr.attribute_key;
      return Boolean(skuAttrFilters[key]);
    });

  const clearSkuFilters = () => {
    setSkuCodeFilter('');
    setSkuAttrFilters({});
  };

  const openVariationComboDialog = useCallback(() => {
    const pid = productKeyForApis;
    if (!pid) {
      return;
    }
    setVariationComboDimIncluded(buildEmptyVariationIncludedMaps(nominalVariationDimsForSku));
    setVariationComboOpen(true);
  }, [productKeyForApis, nominalVariationDimsForSku]);

  const toggleVariationDimOptionIncluded = useCallback((attrKey, optionValue) => {
    const k = String(attrKey || '').trim();
    const v = String(optionValue ?? '').trim();
    if (!k || !v) return;
    setVariationComboDimIncluded((prev) => {
      const nextRec = { ...(prev[k] || {}) };
      nextRec[v] = !nextRec[v];
      return {
        ...prev,
        [k]: nextRec,
      };
    });
  }, []);

  const variationDimIncludeAllValuesForDimension = useCallback((attr) => {
    const ak = attr.key || attr.attribute_key;
    if (!ak) return;
    setVariationComboDimIncluded((prev) => {
      const nextRec = { ...(prev[ak] || {}) };
      attributeOptionStrings(attr).forEach((ov) => {
        nextRec[ov] = true;
      });
      return { ...prev, [ak]: nextRec };
    });
  }, []);

  const variationDimClearSelectionsForDimension = useCallback((attr) => {
    const ak = attr.key || attr.attribute_key;
    if (!ak) return;
    setVariationComboDimIncluded((prev) => {
      const nextRec = { ...(prev[ak] || {}) };
      attributeOptionStrings(attr).forEach((ov) => {
        nextRec[ov] = false;
      });
      return { ...prev, [ak]: nextRec };
    });
  }, []);

  const handleCloseVariationComboDialog = useCallback(() => {
    setVariationComboOpen(false);
  }, []);

  const handleSaveVariationCombinationSelections = useCallback(async () => {
    const pid = productKeyForApis;
    if (!pid) {
      return;
    }
    if (!dimensionSelectionsComplete(nominalVariationDimsForSku, variationComboDimIncluded)) {
      setSnackbar({
        open: true,
        message: translate('products.variation_combo_pick_each_dimension'),
        severity: 'warning',
      });
      return;
    }
    const selectedRows = variationModalCandidateRows.map((row) => ({ ...row }));
    beginPageBusy();
    try {
      await setVariationCombinationSelections(pid, selectedRows);
      setVariationComboOpen(false);
      await loadData(false);
      setSnackbar({
        open: true,
        message: translate('common.saved'),
        severity: 'success',
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message,
        severity: 'error',
      });
    } finally {
      endPageBusy();
    }
  }, [
    productKeyForApis,
    variationModalCandidateRows,
    variationComboDimIncluded,
    nominalVariationDimsForSku,
    beginPageBusy,
    endPageBusy,
    loadData,
    translate,
  ]);

  const handleSkuAttrFilterChange = (attrKey, nextValue) => {
    setSkuAttrFilters((prev) => ({
      ...prev,
      [attrKey]: nextValue ?? '',
    }));
  };

  const handleSkuChange = (skuId, field, value, options = {}) => {
    let processedValue = value;
    if (field === 'price') {
      const cleaned = value.toString().replace(/\D/g, '');
      processedValue = cleaned === '' ? '' : applyCurrencyMask(cleaned);
    } else if (field === 'manufacturing_days') {
      const n = parseInt(value, 10);
      processedValue = value === '' ? '' : (isNaN(n) ? 0 : n);
    }
    setSkus((prev) =>
      prev.map((sku) => {
        if (sku.id !== skuId) return sku;
        const next = { ...sku, [field]: processedValue };
        if (field === 'is_made_to_order' && !processedValue) {
          next.manufacturing_days = 0;
        }
        return next;
      })
    );
    if (skuErrors[`${skuId}_${field}`]) {
      setSkuErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[`${skuId}_${field}`];
        return newErrors;
      });
    }
    if (options.autoSave) {
      const override = field === 'status'
        ? { status: value }
        : field === 'is_made_to_order'
          ? { is_made_to_order: !!value, manufacturing_days: value ? 0 : undefined }
          : {};
      handleSaveSku(skuId, override);
    }
  };

  const validateSku = (skuData, skuId) => {
    const newErrors = {};
    const isMTO = !!skuData.is_made_to_order;

    const priceVal = typeof skuData.price === 'string' ? parseFloat(parseCurrency(skuData.price)) : (skuData.price ?? 0);
    if (isNaN(priceVal) || priceVal < 0) {
      newErrors[`${skuId}_price`] = 'Preço inválido';
    }

    if (isMTO) {
      const days = parseInt(skuData.manufacturing_days, 10);
      if (skuData.manufacturing_days === '' || skuData.manufacturing_days === null || skuData.manufacturing_days === undefined) {
        newErrors[`${skuId}_manufacturing_days`] = translate('products.manufacturing_days_required');
      } else if (isNaN(days) || days < 0) {
        newErrors[`${skuId}_manufacturing_days`] = translate('products.manufacturing_days_invalid');
      }
    } else {
      if (skuData.stock === '' || skuData.stock === null || skuData.stock === undefined) {
        newErrors[`${skuId}_stock`] = 'Estoque é obrigatório';
      } else {
        const stockNum = typeof skuData.stock === 'number'
          ? skuData.stock
          : parseFloat(String(skuData.stock).replace(',', '.'));
        if (!Number.isFinite(stockNum) || stockNum < 0) {
          newErrors[`${skuId}_stock`] = 'Estoque inválido';
        }
      }
    }

    setSkuErrors((prev) => ({ ...prev, ...newErrors }));
    return Object.keys(newErrors).length === 0;
  };

  const handleSaveSku = async (skuId, overrides = {}) => {
    const skuData = { ...skus.find((s) => s.id === skuId), ...overrides };
    if (!skuData.id) return;

    if (!validateSku(skuData, skuId)) {
      return;
    }

    setSavingSkuIds((prev) => new Set([...prev, skuId]));
    try {
      const attributesObj = {};
      const attrs = skuData.attributes;
      const entries = attrs && typeof attrs === 'object' && !Array.isArray(attrs)
        ? Object.entries(attrs).map(([key, value]) => ({ key: String(key), value: value != null ? String(value) : '' }))
        : [];
      entries.forEach((attr) => {
        if (attr.key && attr.key.trim() && attr.value != null && String(attr.value).trim()) {
          attributesObj[attr.key.trim()] = String(attr.value).trim();
        }
      });

      const isMTO = !!skuData.is_made_to_order;
      const priceVal = typeof skuData.price === 'string' ? parseFloat(parseCurrency(skuData.price)) : (skuData.price ?? 0);
      const stockParsed = isMTO
        ? 0
        : (typeof skuData.stock === 'number'
          ? skuData.stock
          : parseFloat(String(skuData.stock ?? '').replace(',', '.')));
      const stockOut = Number.isFinite(stockParsed) ? stockParsed : 0;
      const skuPayload = {
        code: skuData.code || '',
        price: isNaN(priceVal) ? 0 : priceVal,
        price_type: skuData.price_type || 'FIXED',
        stock: stockOut,
        stock_unit: (skuData.stock_unit ?? '').toString(),
        is_made_to_order: isMTO,
        manufacturing_days: isMTO ? (parseInt(skuData.manufacturing_days, 10) || 0) : 0,
        status: skuData.status || 'ACTIVE',
        attributes: attributesObj,
      };

      await updateSKU(skuId, skuPayload);
      setSnackbar({
        open: true,
        message: translate('common.saved'),
        severity: 'success',
      });
      await loadData(false);
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message,
        severity: 'error',
      });
    } finally {
      setSavingSkuIds((prev) => {
        const next = new Set(prev);
        next.delete(skuId);
        return next;
      });
    }
  };

  const openSupplyPurchaseDialog = async (skuRowId) => {
    const pid = productKeyForApis;
    if (!pid || !skuRowId) return;
    setSupplyDialogSkuId(skuRowId);
    setSupplyForm({ quantity: '', unitCost: '', notes: '', purchasedAt: '' });
    setSupplyPurchasesLoading(true);
    try {
      const data = await listSupplyPurchases(pid, skuRowId);
      setSupplyPurchasesList(Array.isArray(data?.supply_purchases) ? data.supply_purchases : []);
    } catch (e) {
      setSupplyPurchasesList([]);
      setSnackbar({ open: true, message: e.message, severity: 'error' });
    } finally {
      setSupplyPurchasesLoading(false);
    }
  };

  const closeSupplyPurchaseDialog = () => {
    setSupplyDialogSkuId(null);
    setSupplyPurchasesList([]);
  };

  const handleSubmitSupplyPurchase = async () => {
    if (!supplyDialogSkuId) return;
    const pid = productKeyForApis;
    const qty = parseFloat(String(supplyForm.quantity).replace(',', '.'));
    const ucRaw = supplyForm.unitCost;
    const uc = typeof ucRaw === 'string' ? parseFloat(parseCurrency(ucRaw)) : Number(ucRaw);
    if (!Number.isFinite(qty) || qty <= 0 || !Number.isFinite(uc) || uc < 0) {
      setSnackbar({ open: true, message: translate('products.supply_purchase_invalid'), severity: 'error' });
      return;
    }
    const body = { quantity: qty, unit_cost: uc, notes: (supplyForm.notes || '').trim() };
    if ((supplyForm.purchasedAt || '').trim()) {
      const d = new Date(supplyForm.purchasedAt);
      if (!Number.isNaN(d.getTime())) {
        body.purchased_at = d.toISOString();
      }
    }
    beginPageBusy();
    try {
      await addSupplyPurchase(pid, supplyDialogSkuId, body);
      setSnackbar({ open: true, message: translate('common.saved'), severity: 'success' });
      setSupplyForm({ quantity: '', unitCost: '', notes: '', purchasedAt: '' });
      const data = await listSupplyPurchases(pid, supplyDialogSkuId);
      setSupplyPurchasesList(Array.isArray(data?.supply_purchases) ? data.supply_purchases : []);
      await loadData(false);
    } catch (e) {
      setSnackbar({ open: true, message: e.message, severity: 'error' });
    } finally {
      endPageBusy();
    }
  };

  const handleDeleteSupplyPurchase = async (purchaseId) => {
    if (!supplyDialogSkuId) return;
    const pid = productKeyForApis;
    beginPageBusy();
    try {
      await deleteSupplyPurchase(pid, supplyDialogSkuId, purchaseId);
      setSnackbar({ open: true, message: translate('common.saved'), severity: 'success' });
      const data = await listSupplyPurchases(pid, supplyDialogSkuId);
      setSupplyPurchasesList(Array.isArray(data?.supply_purchases) ? data.supply_purchases : []);
      await loadData(false);
    } catch (e) {
      setSnackbar({ open: true, message: e.message, severity: 'error' });
    } finally {
      endPageBusy();
    }
  };

  const handleSupplyPurchaseFieldChange = (field, value) => {
    if (field === 'unitCost') {
      const cleaned = value.toString().replace(/\D/g, '');
      setSupplyForm((prev) => ({ ...prev, unitCost: cleaned === '' ? '' : applyCurrencyMask(cleaned) }));
      return;
    }
    setSupplyForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleDeleteProductClick = () => {
    setConfirmDialog({
      open: true,
      type: 'product',
      payload: null,
    });
  };

  const saveAttributeWithPayload = async (attributeId, attributeData, reconcileOpts = {}) => {
    const opts = (attributeData.options || []).map((o) => (typeof o === 'string' ? o : o.value || o)).filter(Boolean);
    const payload = {
      name: attributeData.name.trim(),
      is_customizable: attributeData.is_customizable || false,
      is_variation: !!(attributeData.is_variation || false),
      is_required: attributeData.is_required || false,
      customizable_type: attributeData.is_customizable && attributeData.customizable_type ? attributeData.customizable_type : null,
      allow_other: attributeData.is_customizable && attributeData.customizable_type === 'select' ? (attributeData.allow_other || false) : false,
      display_order: attributeId === 'new' ? productAttributes.length : (attributeData.display_order ?? productAttributes.length),
      options: attributeId === 'new' && opts.length > 0 ? opts : undefined,
    };
    if (payload.options === undefined) delete payload.options;

    if (reconcileOpts.confirmSkuReconcile) {
      payload.confirm_sku_reconcile = true;
    }
    if (attributeId === 'new') {
      const dv = (attributeData.default_option_value || '').trim();
      if (dv) {
        payload.default_option_value = dv;
      }
    } else {
      const did = attributeData.default_option_id;
      payload.default_option_id = did && String(did).trim() !== '' ? did : null;
    }

    if (attributeId === 'new') {
      await createProductAttribute(productId, payload);
      setSnackbar({
        open: true,
        message: 'Atributo criado com sucesso',
        severity: 'success',
      });
      setNewAttribute(null);
    } else {
      await updateProductAttribute(productId, attributeId, payload);
      setSnackbar({
        open: true,
        message: 'Atributo atualizado com sucesso',
        severity: 'success',
      });
      setEditingAttributeId(null);
    }

    await loadData(false);
  };

  const handleConfirmDelete = async () => {
    if (confirmDialogActionLockRef.current) {
      return;
    }
    confirmDialogActionLockRef.current = true;
    const { type, payload } = confirmDialogRef.current;
    setConfirmDialog({ open: false, type: null, payload: null });
    const effectiveProductId = productId || id;

    beginPageBusy();
    try {
      if (type === 'product') {
        await deleteProduct(id);
        setSnackbar({
          open: true,
          message: translate('products.deleted'),
          severity: 'success',
        });
        navigate('/products');
      } else if (type === 'attribute' && payload && effectiveProductId) {
        await deleteProductAttribute(effectiveProductId, payload.attributeId, true);
        setSnackbar({
          open: true,
          message: 'Atributo excluído com sucesso',
          severity: 'success',
        });
        await loadData(false);
      } else if (type === 'option' && payload && effectiveProductId) {
        await deleteProductAttributeOption(effectiveProductId, payload.attributeId, payload.optionId, true);
        setSnackbar({
          open: true,
          message: 'Opção excluída com sucesso',
          severity: 'success',
        });
        await loadData(false);
      } else if (type === 'variation_attribute' && effectiveProductId) {
        const pending = pendingVariationAttributeSaveRef.current;
        pendingVariationAttributeSaveRef.current = null;
        if (pending) {
          await saveAttributeWithPayload(pending.attributeId, pending.attributeData, { confirmSkuReconcile: true });
        }
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message,
        severity: 'error',
      });
    } finally {
      endPageBusy();
      confirmDialogActionLockRef.current = false;
    }
  };

  const handleConfirmCancel = () => {
    pendingVariationAttributeSaveRef.current = null;
    setConfirmDialog({ open: false, type: null, payload: null });
  };

  // Product Attributes Management
  const startNewAttribute = () => {
    setNewAttributeOptionDraft('');
    setNewAttribute({
      name: '',
      is_customizable: false,
      is_variation: false,
      is_required: false,
      customizable_type: '',
      allow_other: false,
      display_order: productAttributes.length,
      options: [],
      default_option_value: '',
    });
  };

  const commitNewAttributeOption = () => {
    const trimmed = newAttributeOptionDraft.trim();
    if (!trimmed) {
      return;
    }
    setNewAttribute((prev) => {
      if (!prev) {
        return prev;
      }
      return { ...prev, options: [...(prev.options || []), trimmed] };
    });
    setNewAttributeOptionDraft('');
    requestAnimationFrame(() => {
      newAttributeOptionInputRef.current?.focus?.();
    });
  };

  const handleNewAttributeOptionKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      commitNewAttributeOption();
    }
  };

  // Drag and Drop handlers
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const optionSensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEndAttributes = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = productAttributes.findIndex((attr) => attr.id === active.id);
    const newIndex = productAttributes.findIndex((attr) => attr.id === over.id);

    const newAttributes = arrayMove(productAttributes, oldIndex, newIndex);
    // Update display_order based on new position
    const updatedAttributes = newAttributes.map((attr, index) => ({
      ...attr,
      display_order: index,
    }));
    
    setProductAttributes(updatedAttributes);

    beginPageBusy();
    try {
      for (let i = 0; i < updatedAttributes.length; i++) {
        const attr = updatedAttributes[i];
        const oldIndex = productAttributes.findIndex((a) => a.id === attr.id);
        if (oldIndex !== i) {
          await updateProductAttribute(productId, attr.id, {
            name: attr.name || attr.key,
            is_customizable: attr.is_customizable,
            is_variation: attr.is_variation,
            is_required: attr.is_required,
            customizable_type: attr.customizable_type || null,
            allow_other: attr.is_customizable && attr.customizable_type === 'select' ? (attr.allow_other || false) : false,
            display_order: i,
          });
        }
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Erro ao atualizar ordem dos atributos',
        severity: 'error',
      });
      await loadData(false);
    } finally {
      endPageBusy();
    }
  };

  const handleDragEndOptions = async (event, attributeId) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const attribute = productAttributes.find((attr) => attr.id === attributeId);
    if (!attribute || !attribute.options) return;

    const oldIndex = attribute.options.findIndex((opt) => opt.id === active.id);
    const newIndex = attribute.options.findIndex((opt) => opt.id === over.id);

    const newOptions = arrayMove(attribute.options, oldIndex, newIndex);
    // Update display_order based on new position
    const updatedOptions = newOptions.map((opt, index) => ({
      ...opt,
      display_order: index,
    }));

    // Update local state immediately for better UX
    setProductAttributes((prev) =>
      prev.map((attr) =>
        attr.id === attributeId ? { ...attr, options: updatedOptions } : attr
      )
    );

  };

  const cancelNewAttribute = () => {
    setNewAttribute(null);
  };

  const handleAttributeChange = (attributeId, field, value) => {
    const applyMutualExclusion = (prev) => {
      const next = { ...prev, [field]: value };
      if (field === 'is_customizable' && value) {
        next.is_variation = false;
      } else if (field === 'is_variation' && value) {
        next.is_customizable = false;
        next.customizable_type = '';
        next.allow_other = false;
      } else if (field === 'customizable_type' && value !== 'select') {
        next.allow_other = false;
      }
      return next;
    };
    if (attributeId === 'new') {
      if (field === 'default_option_value') {
        setNewAttribute((prev) => ({ ...prev, default_option_value: value }));
        return;
      }
      setNewAttribute((prev) => applyMutualExclusion(prev));
    } else {
      setProductAttributes((prev) =>
        prev.map((attr) => (attr.id === attributeId ? applyMutualExclusion(attr) : attr))
      );
    }
  };

  const handleDefaultOptionChange = async (attrId, optionId) => {
    if (!productId) {
      return;
    }
    const attr = productAttributes.find((a) => a.id === attrId);
    if (!attr) {
      return;
    }
    beginPageBusy();
    try {
      await updateProductAttribute(productId, attrId, {
        name: attr.name || attr.key,
        is_customizable: attr.is_customizable,
        is_variation: attr.is_variation,
        is_required: attr.is_required,
        customizable_type: attr.customizable_type || null,
        allow_other: attr.is_customizable && attr.customizable_type === 'select' ? (attr.allow_other || false) : false,
        display_order: attr.display_order ?? 0,
        default_option_id: optionId || null,
      });
      setSnackbar({
        open: true,
        message: translate('common.saved'),
        severity: 'success',
      });
      await loadData(false);
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message,
        severity: 'error',
      });
      await loadData(false);
    } finally {
      endPageBusy();
    }
  };

  const handleSaveAttribute = async (attributeId) => {
    if (!productId) return;

    let attributeData;
    if (attributeId === 'new') {
      attributeData = newAttribute;
    } else {
      attributeData = productAttributes.find((a) => a.id === attributeId);
    }

    if (!attributeData.name || !attributeData.name.trim()) {
      setSnackbar({
        open: true,
        message: translate('products.attribute_name') + ' é obrigatório',
        severity: 'error',
      });
      return;
    }
    if (attributeData.is_customizable && (!attributeData.customizable_type || !attributeData.customizable_type.trim())) {
      setSnackbar({
        open: true,
        message: translate('products.attribute_customizable_type') + ' é obrigatório quando o atributo é customizável',
        severity: 'error',
      });
      return;
    }
    if (attributeData.is_customizable && attributeData.customizable_type === 'select' && attributeId !== 'new') {
      const opts = attributeData.options || [];
      if (opts.length === 0) {
        setSnackbar({
          open: true,
          message: 'Tipo seleção exige ao menos uma opção',
          severity: 'error',
        });
        return;
      }
    }

    const hasExistingSkus = Array.isArray(skus) && skus.length > 0;
    const newOptStrings = (attributeData.options || []).map((o) => (typeof o === 'string' ? o : o.value || o)).filter((s) => (s || '').toString().trim() !== '');
    const optCount =
      attributeId === 'new'
        ? newOptStrings.length
        : (attributeData.options || []).length;
    const hasDefaultValue =
      attributeId === 'new'
        ? (attributeData.default_option_value || '').trim() !== ''
        : !!(attributeData.default_option_id && String(attributeData.default_option_id).trim() !== '');

    const isNewReconcile =
      attributeId === 'new' &&
      optCount > 0 &&
      (attributeData.is_variation || (attributeData.is_required && !attributeData.is_customizable));

    const prevSnap = attributeId !== 'new' ? previousAttributeValuesRef.current[attributeId] : null;
    const turningVariation = !!(prevSnap && !prevSnap.is_variation && attributeData.is_variation);
    const turningRequired = !!(prevSnap && !prevSnap.is_required && attributeData.is_required);

    if (
      attributeId !== 'new' &&
      turningRequired &&
      hasExistingSkus &&
      optCount > 0 &&
      !(attributeData.default_option_id && String(attributeData.default_option_id).trim())
    ) {
      setSnackbar({
        open: true,
        message: translate('products.attribute_default_required_before_required'),
        severity: 'error',
      });
      return;
    }

    const needReconcileDialog =
      hasExistingSkus &&
      !hasDefaultValue &&
      (isNewReconcile || (turningVariation && optCount > 0));

    if (needReconcileDialog) {
      pendingVariationAttributeSaveRef.current = {
        attributeId,
        attributeData: JSON.parse(JSON.stringify(attributeData)),
      };
      setConfirmDialog({
        open: true,
        type: 'variation_attribute',
        payload: {
          attributeId,
          attributeData: JSON.parse(JSON.stringify(attributeData)),
        },
      });
      return;
    }

    beginPageBusy();
    try {
      await saveAttributeWithPayload(attributeId, attributeData);
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message,
        severity: 'error',
      });
    } finally {
      endPageBusy();
    }
  };

  const handleDeleteAttribute = async (attributeId) => {
    if (!productId) return;
    const attribute = productAttributes.find((a) => a.id === attributeId);
    beginPageBusy();
    try {
      const { affected_skus } = await getAffectedSKUsByAttribute(productId, attributeId);
      if (affected_skus && affected_skus.length > 0) {
        setConfirmDialog({
          open: true,
          type: 'attribute',
          payload: {
            attributeId,
            attributeName: attribute?.name || attribute?.key || 'Atributo',
            affectedSkus: affected_skus,
          },
        });
      } else {
        await deleteProductAttribute(productId, attributeId);
        setSnackbar({
          open: true,
          message: 'Atributo excluído com sucesso',
          severity: 'success',
        });
        await loadData(false);
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message,
        severity: 'error',
      });
    } finally {
      endPageBusy();
    }
  };

  const handleAddOption = async (attributeId, value) => {
    if (!productId || !value || !value.trim()) return;
    beginPageBusy();
    try {
      const attribute = productAttributes.find((attr) => attr.id === attributeId);
      const currentOptionsCount = attribute?.options?.length || 0;
      await createProductAttributeOption(productId, attributeId, {
        value: value.trim(),
        display_order: currentOptionsCount,
      });
      await loadData(false);
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message,
        severity: 'error',
      });
    } finally {
      endPageBusy();
    }
  };

  const handleDeleteOption = async (attributeId, optionId) => {
    const effectiveProductId = productId || id;
    if (!effectiveProductId) return;
    beginPageBusy();
    try {
      await deleteProductAttributeOption(effectiveProductId, attributeId, optionId);
      setSnackbar({
        open: true,
        message: 'Opção excluída com sucesso',
        severity: 'success',
      });
      await loadData(false);
    } catch (error) {
      if (error?.message?.toLowerCase().includes('skus')) {
        let affectedSkus = [];
        try {
          const response = await getAffectedSKUsByOption(effectiveProductId, attributeId, optionId);
          affectedSkus = response?.affected_skus ?? response?.data?.affected_skus ?? [];
        } catch (_) {}
        const attribute = productAttributes.find((a) => a.id === attributeId);
        const option = attribute?.options?.find((o) => o.id === optionId);
        setConfirmDialog({
          open: true,
          type: 'option',
          payload: {
            attributeId,
            optionId,
            attributeName: attribute?.name || attribute?.key || 'Atributo',
            optionValue: option?.value || 'Opção',
            affectedSkus: Array.isArray(affectedSkus) ? affectedSkus : [],
          },
        });
      } else {
        setSnackbar({
          open: true,
          message: error.message,
          severity: 'error',
        });
      }
    } finally {
      endPageBusy();
    }
  };

  const getPriceDisplayValue = (sku) => {
    if (sku.price === '' || sku.price === null || sku.price === undefined) return '';
    if (typeof sku.price === 'number') {
      return applyCurrencyMask(Math.round(sku.price * 100).toString());
    }
    return sku.price;
  };

  const applySimulatedPriceToSku = (skuId, price) => {
    const n = Number(price);
    if (!Number.isFinite(n) || n < 0) return;
    const masked = applyCurrencyMask(Math.round(n * 100).toString());
    handleSkuChange(skuId, 'price', masked);
    handleSaveSku(skuId, { price: masked });
  };

  const renderSkuMobileCard = (sku) => {
    const skuId = sku.id;
    const isDefault = sku.is_default || false;
    const isSaving = savingSkuIds.has(skuId);

    return (
      <Paper key={skuId} variant="outlined" sx={{ p: 2, borderRadius: 1, width: '100%' }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mb: 1.5 }}>
          <Typography
            variant="subtitle1"
            component="div"
            sx={{ fontFamily: 'monospace', fontWeight: 600, wordBreak: 'break-all', flex: '1 1 100%' }}
          >
            {sku.code}
          </Typography>
          {isDefault && (
            <Chip label="Padrão" size="small" color="primary" sx={{ height: 22, fontSize: '0.7rem' }} />
          )}
        </Box>
        {variationAttributes.length > 0 && (
          <Stack spacing={0} sx={{ mb: 2 }}>
            {variationAttributes.map((attr) => {
              const attrKey = attr.key || attr.attribute_key;
              const cellValue = getSkuAttributeValueForKey(sku.attributes, attrKey);
              return (
                <Box
                  key={attr.id}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    gap: 2,
                    py: 1,
                    borderBottom: 1,
                    borderColor: 'divider',
                    '&:first-of-type': { pt: 0 },
                    '&:last-of-type': { borderBottom: 0, pb: 0 },
                  }}
                >
                  <Typography variant="body2" color="text.secondary" sx={{ flex: '0 1 45%' }}>
                    {attr.name || attr.key}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500, textAlign: 'right', flex: '1 1 55%', wordBreak: 'break-word' }}>
                    {cellValue ?? '-'}
                  </Typography>
                </Box>
              );
            })}
          </Stack>
        )}
        <TextField
          fullWidth
          size="small"
          label={translate('products.price')}
          value={getPriceDisplayValue(sku)}
          onChange={(e) => handleSkuChange(skuId, 'price', e.target.value)}
          onBlur={() => handleSaveSku(skuId)}
          error={!!skuErrors[`${skuId}_price`]}
          helperText={skuErrors[`${skuId}_price`]}
          disabled={isSaving}
          sx={{ mb: 1.5 }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end" sx={{ ml: 0, maxHeight: 32 }}>
                <ExtensionSlot
                  slotId="product.sku.price"
                  context={{
                    description: [formData.name, sku.code].filter(Boolean).join(' — '),
                    quantity: 1,
                    onSetPrice: (p) => applySimulatedPriceToSku(skuId, p),
                  }}
                />
              </InputAdornment>
            ),
          }}
        />
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
          <FormControlLabel
            control={(
              <Checkbox
                checked={(sku.status || 'ACTIVE') === 'ACTIVE'}
                onChange={(e) => handleSkuChange(skuId, 'status', e.target.checked ? 'ACTIVE' : 'INACTIVE', { autoSave: true })}
                disabled={isSaving}
              />
            )}
            label={translate('products.active')}
          />
          <FormControlLabel
            control={(
              <Checkbox
                checked={!!sku.is_made_to_order}
                onChange={(e) => handleSkuChange(skuId, 'is_made_to_order', e.target.checked, { autoSave: true })}
                disabled={isSaving}
              />
            )}
            label={translate('products.made_to_order_label')}
          />
        </Box>
        <Divider sx={{ my: 1.5 }} />
        <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 0.75, letterSpacing: 0.5 }}>
          {!sku.is_made_to_order
            ? translate('products.sku_mobile_stock_section')
            : translate('products.sku_mobile_fulfillment_section')}
        </Typography>
        {!sku.is_made_to_order ? (
          <TextField
            fullWidth
            size="small"
            type="number"
            label={translate('products.stock_current')}
            value={sku.stock ?? ''}
            onChange={(e) => handleSkuChange(skuId, 'stock', e.target.value)}
            onBlur={() => handleSaveSku(skuId)}
            error={!!skuErrors[`${skuId}_stock`]}
            helperText={skuErrors[`${skuId}_stock`]}
            disabled={isSaving}
            inputProps={{ min: 0, step: 'any' }}
            InputLabelProps={{ shrink: true }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end" sx={{ pr: 0.25 }}>
                  <Tooltip title={translate('products.supply_purchase_add')}>
                    <span>
                      <IconButton
                        size="small"
                        edge="end"
                        color="primary"
                        onClick={() => openSupplyPurchaseDialog(skuId)}
                        disabled={isSaving}
                        aria-label={translate('products.supply_purchase_add')}
                      >
                        <AddIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </InputAdornment>
              ),
            }}
          />
        ) : (
          <TextField
            fullWidth
            size="small"
            type="number"
            label={translate('products.sku_col_prazo_dias')}
            value={sku.manufacturing_days ?? ''}
            onChange={(e) => handleSkuChange(skuId, 'manufacturing_days', e.target.value)}
            onBlur={() => handleSaveSku(skuId)}
            error={!!skuErrors[`${skuId}_manufacturing_days`]}
            helperText={skuErrors[`${skuId}_manufacturing_days`]}
            disabled={isSaving}
            inputProps={{ min: 0 }}
          />
        )}
      </Paper>
    );
  };

  const renderSkuRow = (sku) => {
    const skuId = sku.id;
    const isDefault = sku.is_default || false;
    const isSaving = savingSkuIds.has(skuId);

    return (
      <TableRow key={skuId} hover sx={{ '& > .MuiTableCell-root': { py: 1.25, verticalAlign: 'middle' } }}>
        <TableCell sx={{ verticalAlign: 'middle' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap' }}>
            {sku.code}
            {isDefault && (
              <Chip
                label="Padrão"
                size="small"
                color="primary"
                sx={{ height: 20, fontSize: '0.7rem', flexShrink: 0 }}
              />
            )}
          </Box>
        </TableCell>
        {variationAttributes.map((attr) => {
          const attrKey = attr.key || attr.attribute_key;
          const cellValue = getSkuAttributeValueForKey(sku.attributes, attrKey);
          return (
            <TableCell
              key={attr.id}
              sx={{
                verticalAlign: 'middle',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: 168,
              }}
              title={cellValue ?? undefined}
            >
              {cellValue ?? '-'}
            </TableCell>
          );
        })}
        <TableCell
          sx={{
            verticalAlign: 'middle',
            width: skuTablePriceColWidthPx,
            maxWidth: skuTablePriceColWidthPx,
            px: 1,
            boxSizing: 'border-box',
          }}
        >
          <TextField
            size="small"
            fullWidth
            value={getPriceDisplayValue(sku)}
            onChange={(e) => handleSkuChange(skuId, 'price', e.target.value)}
            onBlur={() => handleSaveSku(skuId)}
            error={!!skuErrors[`${skuId}_price`]}
            helperText={skuErrors[`${skuId}_price`] || undefined}
            disabled={isSaving}
            inputProps={{ 'aria-label': translate('products.price') }}
            sx={{ ...skuGridInputSx }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end" sx={{ ml: 0, maxHeight: 32 }}>
                  <ExtensionSlot
                    slotId="product.sku.price"
                    context={{
                      description: [formData.name, sku.code].filter(Boolean).join(' — '),
                      quantity: 1,
                      onSetPrice: (p) => applySimulatedPriceToSku(skuId, p),
                    }}
                  />
                </InputAdornment>
              ),
            }}
          />
        </TableCell>
        <TableCell
          align="center"
          sx={{
            verticalAlign: 'middle',
            width: skuTableToggleColWidthPx,
            maxWidth: skuTableToggleColWidthPx,
            px: 0.5,
            boxSizing: 'border-box',
          }}
        >
          <Checkbox
            checked={(sku.status || 'ACTIVE') === 'ACTIVE'}
            onChange={(e) => handleSkuChange(skuId, 'status', e.target.checked ? 'ACTIVE' : 'INACTIVE', { autoSave: true })}
            disabled={isSaving}
            sx={{ p: 0.5 }}
          />
        </TableCell>
        <TableCell
          align="center"
          sx={{
            verticalAlign: 'middle',
            width: skuTableMtoColWidthPx,
            maxWidth: skuTableMtoColWidthPx,
            px: 0.5,
            boxSizing: 'border-box',
          }}
        >
          <Tooltip title={translate('products.made_to_order')}>
            <Checkbox
              checked={!!sku.is_made_to_order}
              onChange={(e) => handleSkuChange(skuId, 'is_made_to_order', e.target.checked, { autoSave: true })}
              disabled={isSaving}
              inputProps={{ 'aria-label': translate('products.made_to_order_label') }}
              sx={{ p: 0.5 }}
            />
          </Tooltip>
        </TableCell>
        <TableCell
          sx={{
            verticalAlign: 'middle',
            width: skuTableValueColWidthPx,
            maxWidth: skuTableValueColWidthPx,
            pl: 1,
            pr: 1,
            boxSizing: 'border-box',
          }}
        >
          <Box
            sx={{
              width: skuValueRowBoxPx,
              maxWidth: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
              gap: 0.5,
            }}
          >
            {sku.is_made_to_order ? (
              <TextField
                size="small"
                variant="outlined"
                type="number"
                placeholder={translate('products.sku_col_prazo_dias')}
                value={sku.manufacturing_days ?? ''}
                onChange={(e) => handleSkuChange(skuId, 'manufacturing_days', e.target.value)}
                onBlur={() => handleSaveSku(skuId)}
                error={!!skuErrors[`${skuId}_manufacturing_days`]}
                helperText={skuErrors[`${skuId}_manufacturing_days`] || undefined}
                disabled={isSaving}
                inputProps={{ min: 0, 'aria-label': translate('products.sku_col_prazo_dias') }}
                sx={{ width: skuPrazoInputPx, flexShrink: 0, ...skuGridInputSx }}
              />
            ) : (
              <>
                <TextField
                  size="small"
                  variant="outlined"
                  type="number"
                  placeholder={translate('products.stock_current')}
                  value={sku.stock ?? ''}
                  onChange={(e) => handleSkuChange(skuId, 'stock', e.target.value)}
                  onBlur={() => handleSaveSku(skuId)}
                  error={!!skuErrors[`${skuId}_stock`]}
                  helperText={skuErrors[`${skuId}_stock`] || undefined}
                  disabled={isSaving}
                  inputProps={{
                    min: 0,
                    step: 'any',
                    'aria-label': translate('products.stock_current'),
                  }}
                  sx={{ width: skuStockInputPx, flexShrink: 0, ...skuGridInputSx }}
                />
                <Tooltip title={translate('products.supply_purchase_add')}>
                  <span>
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => openSupplyPurchaseDialog(skuId)}
                      disabled={isSaving}
                      aria-label={translate('products.supply_purchase_add')}
                      sx={{ flexShrink: 0, width: 32, height: 32, p: 0.5 }}
                    >
                      <AddIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </span>
                </Tooltip>
              </>
            )}
          </Box>
        </TableCell>
      </TableRow>
    );
  };

  if (loadingData) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, minHeight: 36 }}>
          <ExtensionSlot
            slotId="product.form.header"
            context={{
              productId: id,
              isEdit,
              productName: formData.name,
            }}
          />
        </Box>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          {isEdit && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={handleDeleteProductClick}
            >
              Excluir Produto
            </Button>
          )}
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={blockingOverlayOpen}
          >
            {blockingOverlayOpen ? translate('common.saving') : translate('common.save')}
          </Button>
          <Button
            variant="outlined"
            onClick={() => navigate('/products')}
          >
            {translate('common.cancel')}
          </Button>
        </Box>
      </Box>

      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mb: 2 }}>
          <Box sx={{ flex: { xs: '1', sm: '0.5' } }}>
            <TextField
              fullWidth
              label={translate('products.name')}
              name="name"
              value={formData.name}
              onChange={handleChange}
              error={!!errors.name}
              helperText={errors.name}
              required
            />
          </Box>
          {isEdit && formData.code && (
            <Box sx={{ flex: { xs: '1', sm: '0.2' } }}>
              <TextField
                fullWidth
                label={translate('products.code')}
                value={formData.code}
                InputProps={{ readOnly: true }}
                sx={{ '& .MuiInputBase-root': { backgroundColor: 'action.hover' } }}
              />
            </Box>
          )}
          <Box sx={{ flex: { xs: '1', sm: '0.25' } }}>
            <TextField
              fullWidth
              select
              label={translate('products.item_type')}
              name="type"
              value={formData.type || 'PRODUCT'}
              onChange={handleChange}
            >
              <MenuItem value="PRODUCT">{translate('products.item_type_product')}</MenuItem>
              <MenuItem value="SERVICE">{translate('products.item_type_service')}</MenuItem>
              <MenuItem value="SUPPLY">{translate('products.item_type_supply')}</MenuItem>
            </TextField>
          </Box>
          <Box sx={{ flex: { xs: '1', sm: '0.25' } }}>
            <TextField
              fullWidth
              select
              label={translate('products.status')}
              name="status"
              value={formData.status}
              onChange={handleChange}
              error={!!errors.status}
              helperText={errors.status}
              required
            >
              <MenuItem value="ACTIVE">{translate('products.active')}</MenuItem>
              <MenuItem value="INACTIVE">{translate('products.inactive')}</MenuItem>
            </TextField>
          </Box>
        </Box>
        <Box>
          <TextField
            fullWidth
            label={translate('products.description')}
            name="description"
            value={formData.description}
            onChange={handleChange}
            error={!!errors.description}
            helperText={errors.description}
            multiline
            rows={4}
          />
        </Box>
      </Box>

      {isEdit && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" component="h2" gutterBottom>
            {translate('products.image_gallery')}
          </Typography>
          <MediaGallery
            metadataKey="product_image"
            metadataValue={productId || id}
            accept="image/*"
            onMediaChange={(medias) => {
              console.log('Galeria atualizada:', medias);
            }}
          />
        </Box>
      )}

      {isEdit && (
        <>
          <Box sx={{ mt: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h5">
                Atributos do Produto
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={startNewAttribute}
                disabled={!!newAttribute}
              >
                {translate('products.attribute_add')}
              </Button>
            </Box>

            {newAttribute && (
              <Paper sx={{ p: 2, mb: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={3}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <TextField
                        fullWidth
                        label={translate('products.attribute_name')}
                        value={newAttribute.name}
                        onChange={(e) => handleAttributeChange('new', 'name', e.target.value)}
                        placeholder="Ex: Cor, Tamanho"
                      />
                      <FieldHelpIcon title={translate('products.attribute_name_help')} />
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={newAttribute.is_variation}
                          onChange={(e) => handleAttributeChange('new', 'is_variation', e.target.checked)}
                          disabled={newAttribute.is_customizable}
                        />
                      }
                      label={
                        <Box component="span" sx={{ display: 'flex', alignItems: 'center' }}>
                          {translate('products.attribute_variation')}
                          <FieldHelpIcon title={translate('products.attribute_variation_help')} />
                        </Box>
                      }
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={newAttribute.is_customizable}
                          onChange={(e) => handleAttributeChange('new', 'is_customizable', e.target.checked)}
                          disabled={newAttribute.is_variation}
                        />
                      }
                      label={
                        <Box component="span" sx={{ display: 'flex', alignItems: 'center' }}>
                          {translate('products.attribute_customizable')}
                          <FieldHelpIcon title={translate('products.attribute_customizable_help')} />
                        </Box>
                      }
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={newAttribute.is_required}
                          onChange={(e) => handleAttributeChange('new', 'is_required', e.target.checked)}
                        />
                      }
                      label={
                        <Box component="span" sx={{ display: 'flex', alignItems: 'center' }}>
                          {translate('products.attribute_required')}
                          <FieldHelpIcon title={translate('products.attribute_required_help')} />
                        </Box>
                      }
                    />
                  </Grid>
                  {newAttribute.is_customizable && (
                    <Grid item xs={12} md={2}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }} data-testid="attribute-customizable-type-wrapper">
                        <TextField
                          select
                          fullWidth
                          size="small"
                          label={translate('products.attribute_customizable_type')}
                          value={newAttribute.customizable_type || ''}
                          onChange={(e) => handleAttributeChange('new', 'customizable_type', e.target.value)}
                        >
                          <MenuItem value="text">Texto</MenuItem>
                          <MenuItem value="number">Número</MenuItem>
                          <MenuItem value="date">Data</MenuItem>
                          <MenuItem value="textarea">Texto longo</MenuItem>
                          <MenuItem value="image">Imagem</MenuItem>
                          <MenuItem value="select">Seleção</MenuItem>
                        </TextField>
                        <FieldHelpIcon title={translate('products.attribute_customizable_type_help')} />
                      </Box>
                    </Grid>
                  )}
                  {newAttribute.is_customizable && newAttribute.customizable_type === 'select' && (
                    <Grid item xs={12} md={2}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={newAttribute.allow_other || false}
                            onChange={(e) => handleAttributeChange('new', 'allow_other', e.target.checked)}
                          />
                        }
                        label={
                          <Box component="span" sx={{ display: 'flex', alignItems: 'center' }}>
                            {translate('products.attribute_allow_other')}
                            <FieldHelpIcon title={translate('products.attribute_allow_other_help')} />
                          </Box>
                        }
                      />
                    </Grid>
                  )}
                  {(newAttribute.is_variation || (newAttribute.is_customizable && newAttribute.customizable_type === 'select')) && (
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                        {(newAttribute.options || []).map((opt, idx) => (
                          <Chip
                            key={idx}
                            label={typeof opt === 'string' ? opt : opt.value}
                            size="small"
                            onDelete={() =>
                              setNewAttribute((prev) => ({
                                ...prev,
                                options: (prev.options || []).filter((_, i) => i !== idx),
                              }))
                            }
                          />
                        ))}
                        <Stack
                          direction={{ xs: 'column', sm: 'row' }}
                          spacing={1}
                          sx={{ alignItems: { xs: 'stretch', sm: 'center' }, width: { xs: '100%', sm: 'auto' }, flexBasis: { xs: '100%', sm: 'auto' } }}
                        >
                          <TextField
                            inputRef={newAttributeOptionInputRef}
                            size="small"
                            placeholder="Nova opção"
                            value={newAttributeOptionDraft}
                            onChange={(e) => setNewAttributeOptionDraft(e.target.value)}
                            onKeyDown={handleNewAttributeOptionKeyDown}
                            inputProps={{ 'data-testid': 'attribute-new-option-input' }}
                            sx={{ minWidth: { sm: 140 }, flex: { sm: '1 1 140px' } }}
                            fullWidth
                          />
                          <Button
                            size="small"
                            variant="contained"
                            disabled={!newAttributeOptionDraft.trim()}
                            onClick={commitNewAttributeOption}
                          >
                            {translate('common.add')}
                          </Button>
                        </Stack>
                      </Box>
                    </Grid>
                  )}
                  {(newAttribute.is_variation || (newAttribute.is_required && !newAttribute.is_customizable)) &&
                    (newAttribute.options || []).length > 0 && (
                    <Grid item xs={12} md={4}>
                      <TextField
                        select
                        fullWidth
                        size="small"
                        label={translate('products.attribute_default_option')}
                        value={newAttribute.default_option_value || ''}
                        onChange={(e) => handleAttributeChange('new', 'default_option_value', e.target.value)}
                        helperText={translate('products.attribute_default_option_help')}
                      >
                        <MenuItem value="">
                          <em>—</em>
                        </MenuItem>
                        {(newAttribute.options || []).map((o) => (
                          <MenuItem key={o} value={o}>
                            {o}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                  )}
                  <Grid item xs={12} md={newAttribute.is_customizable && newAttribute.customizable_type === 'select' ? 1 : (newAttribute.is_customizable ? 1 : 5)}>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => handleSaveAttribute('new')}
                      >
                        Salvar
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={cancelNewAttribute}
                      >
                        Cancelar
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </Paper>
            )}

            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEndAttributes}
            >
              <SortableContext
                items={productAttributes.map(attr => attr.id)}
                strategy={verticalListSortingStrategy}
              >
                {productAttributes.map((attr) => (
                  <SortableAttributeItem
                    key={attr.id}
                    attribute={attr}
                    onDelete={handleDeleteAttribute}
                    onAddOption={handleAddOption}
                    editingOptionAttributeId={editingOptionAttributeId}
                    setEditingOptionAttributeId={setEditingOptionAttributeId}
                    handleDeleteOption={handleDeleteOption}
                    onDragEndOptions={handleDragEndOptions}
                    optionSensors={optionSensors}
                    isEditing={editingAttributeId === attr.id}
                    onEdit={() => setEditingAttributeId(attr.id)}
                    onSaveEdit={(aid) => handleSaveAttribute(aid)}
                    onCancelEdit={() => setEditingAttributeId(null)}
                    onAttributeChange={handleAttributeChange}
                    onDefaultOptionChange={handleDefaultOptionChange}
                  />
                ))}
              </SortableContext>
            </DndContext>

            {productAttributes.length === 0 && !newAttribute && (
              <Alert severity="info">
                {translate('products.empty_attributes_hint')}
              </Alert>
            )}
          </Box>

          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
              <Typography variant="h5" component="h2" gutterBottom={isXsBreakpoint}>
                SKUs
              </Typography>
              {isEdit && nominalVariationDimsForSku.length > 0 && (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={openVariationComboDialog}
                  disabled={blockingOverlayOpen}
                >
                  {translate('products.variation_combo_button')}
                </Button>
              )}
            </Box>
            {isXsBreakpoint && skus.length > 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ display: 'block', maxWidth: '100%' }}>
                {translate('products.sku_list_mobile_hint')}
              </Typography>
            )}
          </Box>

          {skus.length > 0 && (
            <Stack
              direction="row"
              flexWrap="wrap"
              useFlexGap
              spacing={1}
              alignItems="center"
              sx={{ mb: 2 }}
            >
              <TextField
                size="small"
                label="Filtrar por código"
                value={skuCodeFilter}
                onChange={(e) => setSkuCodeFilter(e.target.value)}
                sx={{ minWidth: 160 }}
              />
              {variationAttributes.map((attr) => {
                const attrKey = attr.key || attr.attribute_key;
                const opts = skuAttrFilterOptions[attrKey] ?? [];
                const selectedAttr = skuAttrFilters[attrKey] ?? '';
                const menuOpts =
                  selectedAttr && !opts.includes(selectedAttr) ? [selectedAttr, ...opts] : opts;
                return (
                  <FormControl key={attr.id} size="small" sx={{ minWidth: 168, maxWidth: 220 }}>
                    <InputLabel id={`sku-filter-${attr.id}`}>{attr.name || attr.key}</InputLabel>
                    <Select
                      labelId={`sku-filter-${attr.id}`}
                      label={attr.name || attr.key}
                      value={selectedAttr && menuOpts.includes(selectedAttr) ? selectedAttr : ''}
                      onChange={(e) => handleSkuAttrFilterChange(attrKey, e.target.value)}
                    >
                      <MenuItem value="">
                        <em>Todos</em>
                      </MenuItem>
                      {menuOpts.map((optVal) => (
                        <MenuItem key={`${attr.id}-${optVal}`} value={optVal}>
                          {optVal}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                );
              })}
              <Box sx={{ flexGrow: 1, minWidth: 8 }} />
              <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                {filteredSkus.length} de {skus.length} SKUs
              </Typography>
              {skuFiltersActive && (
                <Button size="small" variant="text" onClick={clearSkuFilters}>
                  Limpar filtros
                </Button>
              )}
            </Stack>
          )}

          {isXsBreakpoint ? (
            <Stack spacing={2} sx={{ mt: 2, width: '100%' }}>
              {skus.length === 0 ? (
                <Alert severity="info">
                  {translate('products.empty_skus_hint')}
                </Alert>
              ) : filteredSkus.length === 0 ? (
                <Alert severity="warning">Nenhum SKU corresponde aos filtros selecionados.</Alert>
              ) : (
                filteredSkus.map((sku) => renderSkuMobileCard(sku))
              )}
            </Stack>
          ) : (
            <TableContainer
              component={Paper}
              sx={{
                mt: 2,
                maxWidth: '100%',
                maxHeight: { xs: 'min(520px, calc(100vh - 200px))', sm: 'min(560px, calc(100vh - 240px))' },
                overflow: 'auto',
                scrollbarGutter: 'stable',
              }}
            >
              <Table
                stickyHeader
                size="small"
                sx={{
                  tableLayout: 'fixed',
                  width: '100%',
                  borderCollapse: 'separate',
                  borderSpacing: 0,
                  '& .MuiTableCell-head': {
                    top: 0,
                    backgroundColor: (t) => t.palette.background.paper,
                    zIndex: 2,
                    borderBottom: (t) => `1px solid ${t.palette.divider}`,
                  },
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>{translate('products.code')}</TableCell>
                    {variationAttributes.map((attr) => (
                      <TableCell
                        key={attr.id}
                        sx={{
                          fontWeight: 600,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          maxWidth: 168,
                        }}
                      >
                        {attr.name || attr.key}
                      </TableCell>
                    ))}
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        whiteSpace: 'nowrap',
                        width: skuTablePriceColWidthPx,
                        maxWidth: skuTablePriceColWidthPx,
                        boxSizing: 'border-box',
                      }}
                    >
                      {translate('products.price')}
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{ fontWeight: 600, width: skuTableToggleColWidthPx, maxWidth: skuTableToggleColWidthPx, boxSizing: 'border-box' }}
                    >
                      {translate('products.active')}
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        fontWeight: 600,
                        verticalAlign: 'middle',
                        width: skuTableMtoColWidthPx,
                        maxWidth: skuTableMtoColWidthPx,
                        px: 0.5,
                        py: 1,
                        boxSizing: 'border-box',
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'flex-end',
                          lineHeight: 1.15,
                          gap: 0.15,
                          mx: 'auto',
                          maxWidth: 64,
                        }}
                      >
                        <Typography component="span" variant="caption" sx={{ fontWeight: 600, textTransform: 'none' }}>
                          {translate('products.sku_col_sob_demanda_line1')}
                        </Typography>
                        <Typography component="span" variant="caption" sx={{ fontWeight: 600, textTransform: 'none' }}>
                          {translate('products.sku_col_sob_demanda_line2')}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell
                      sx={{
                        width: skuTableValueColWidthPx,
                        maxWidth: skuTableValueColWidthPx,
                        px: 1,
                        py: 1,
                        verticalAlign: 'middle',
                        boxSizing: 'border-box',
                      }}
                    />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loadingData ? (
                    <TableRow>
                      <TableCell colSpan={skuTableColumnCount} align="center">
                        {translate('common.loading')}
                      </TableCell>
                    </TableRow>
                  ) : skus.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={skuTableColumnCount} align="center">
                        {translate('products.empty_skus_hint')}
                      </TableCell>
                    </TableRow>
                  ) : filteredSkus.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={skuTableColumnCount} align="center">
                        Nenhum SKU corresponde aos filtros selecionados.
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredSkus.map((sku) => renderSkuRow(sku))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}

      <Dialog
        open={variationComboOpen}
        onClose={blockingOverlayOpen ? undefined : handleCloseVariationComboDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{translate('products.variation_combo_title')}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 0.5 }}>
            <Typography variant="body2" color="text.secondary">{translate('products.variation_combo_hint')}</Typography>

            <Stack spacing={2}>
              {nominalVariationDimsForSku.map((attr) => {
                const ak = attr.key || attr.attribute_key;
                const vals = attributeOptionStrings(attr);
                return (
                  <Paper key={ak} variant="outlined" sx={{ p: 1.5 }}>
                    <Stack
                      direction={{ xs: 'column', sm: 'row' }}
                      justifyContent="space-between"
                      alignItems={{ xs: 'flex-start', sm: 'center' }}
                      spacing={1}
                      sx={{ mb: 1 }}
                    >
                      <Typography variant="subtitle2">{attr.name || ak}</Typography>
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        <Button size="small" variant="text" onClick={() => variationDimIncludeAllValuesForDimension(attr)}>
                          {translate('products.variation_combo_dim_all_options')}
                        </Button>
                        <Button size="small" variant="text" color="warning" onClick={() => variationDimClearSelectionsForDimension(attr)}>
                          {translate('products.variation_combo_dim_clear_options')}
                        </Button>
                      </Stack>
                    </Stack>
                    <Stack direction="row" spacing={2} alignItems="center" sx={{ px: { xs: 0, md: 0.75 } }} flexWrap="wrap" useFlexGap rowGap={0.5}>
                      {vals.map((ov) => (
                        <FormControlLabel
                          key={`${ak}:${ov}`}
                          sx={{ mr: 0, ml: 0 }}
                          control={(
                            <Checkbox
                              size="small"
                              checked={Boolean(variationComboDimIncluded[ak]?.[ov])}
                              onChange={() => toggleVariationDimOptionIncluded(ak, ov)}
                            />
                          )}
                          label={<Typography variant="body2">{ov}</Typography>}
                        />
                      ))}
                    </Stack>
                  </Paper>
                );
              })}
            </Stack>

            {!dimensionSelectionsComplete(nominalVariationDimsForSku, variationComboDimIncluded) ? (
              <Alert severity="info">{translate('products.variation_combo_pick_each_dimension')}</Alert>
            ) : (
              <Typography variant="caption" color="text.secondary">
                {`${variationModalCandidateRows.length} ${translate('products.variation_combo_count_suffix')}`}
              </Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleCloseVariationComboDialog} disabled={blockingOverlayOpen}>{translate('common.cancel')}</Button>
          <Button variant="contained" onClick={handleSaveVariationCombinationSelections} disabled={blockingOverlayOpen}>{translate('common.save')}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(supplyDialogSkuId)} onClose={blockingOverlayOpen ? undefined : closeSupplyPurchaseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{translate('products.supply_purchase_title')}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 0.5 }}>
            {supplyDialogSkuId && (
              <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                {skus.find((row) => row.id === supplyDialogSkuId)?.code ?? supplyDialogSkuId}
              </Typography>
            )}
            <Typography variant="subtitle2">{translate('products.supply_purchase_add')}</Typography>
            <TextField
              fullWidth
              size="small"
              type="number"
              label={translate('products.supply_purchase_quantity')}
              value={supplyForm.quantity}
              onChange={(e) => handleSupplyPurchaseFieldChange('quantity', e.target.value)}
              disabled={blockingOverlayOpen}
              inputProps={{ min: 0, step: 'any' }}
            />
            <TextField
              fullWidth
              size="small"
              label={translate('products.supply_purchase_unit_cost')}
              value={supplyForm.unitCost}
              onChange={(e) => handleSupplyPurchaseFieldChange('unitCost', e.target.value)}
              disabled={blockingOverlayOpen}
            />
            <TextField
              fullWidth
              size="small"
              type="datetime-local"
              label={translate('products.supply_purchase_date')}
              value={supplyForm.purchasedAt}
              onChange={(e) => handleSupplyPurchaseFieldChange('purchasedAt', e.target.value)}
              disabled={blockingOverlayOpen}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              fullWidth
              size="small"
              label={translate('products.supply_purchase_notes')}
              value={supplyForm.notes}
              onChange={(e) => handleSupplyPurchaseFieldChange('notes', e.target.value)}
              disabled={blockingOverlayOpen}
              multiline
              minRows={2}
            />
            <Typography variant="subtitle2" sx={{ pt: 1 }}>{translate('products.supply_purchase_history')}</Typography>
            {supplyPurchasesLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={28} />
              </Box>
            ) : supplyPurchasesList.length === 0 ? (
              <Typography variant="body2" color="text.secondary">{translate('products.supply_no_purchases')}</Typography>
            ) : (
              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 260 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>{translate('products.supply_purchase_date')}</TableCell>
                      <TableCell>{translate('products.supply_purchase_quantity')}</TableCell>
                      <TableCell>{translate('products.supply_purchase_unit_cost')}</TableCell>
                      <TableCell>{translate('products.supply_purchase_total')}</TableCell>
                      <TableCell align="right">{translate('common.actions')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {supplyPurchasesList.map((purchase) => (
                      <TableRow key={purchase.id}>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          {purchase.purchased_at ? new Date(purchase.purchased_at).toLocaleString() : '—'}
                        </TableCell>
                        <TableCell>{purchase.quantity}</TableCell>
                        <TableCell>{formatCurrency(purchase.unit_cost)}</TableCell>
                        <TableCell>{formatCurrency(purchase.total_cost)}</TableCell>
                        <TableCell align="right">
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteSupplyPurchase(purchase.id)}
                            disabled={blockingOverlayOpen}
                            aria-label={translate('common.delete')}
                            color="error"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeSupplyPurchaseDialog} disabled={blockingOverlayOpen}>{translate('common.close')}</Button>
          <Button variant="contained" onClick={handleSubmitSupplyPurchase} disabled={blockingOverlayOpen}>
            {translate('products.supply_purchase_submit')}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={confirmDialog.open}
        title={
          confirmDialog.type === 'product'
            ? 'Confirmar exclusão de produto'
            : confirmDialog.type === 'attribute'
            ? `Excluir atributo "${confirmDialog.payload?.attributeName || ''}"`
            : confirmDialog.type === 'option'
            ? `Excluir opção "${confirmDialog.payload?.optionValue || ''}" do atributo "${confirmDialog.payload?.attributeName || ''}"`
            : confirmDialog.type === 'variation_attribute'
            ? translate('products.sku_reconcile_new_required_title')
            : 'Confirmar'
        }
        message={
          confirmDialog.type === 'product'
            ? 'Tem certeza que deseja excluir este produto? Esta ação não pode ser desfeita.'
            : confirmDialog.type === 'variation_attribute'
            ? translate('products.sku_reconcile_new_required_message')
            : (confirmDialog.type === 'attribute' || confirmDialog.type === 'option') && confirmDialog.payload?.affectedSkus?.length > 0
            ? `${confirmDialog.payload.affectedSkus.length} SKU(s) serão excluídos. Revise a lista abaixo antes de confirmar:`
            : 'Tem certeza que deseja excluir?'
        }
        detail={
          (confirmDialog.type === 'attribute' || confirmDialog.type === 'option') &&
          confirmDialog.payload?.affectedSkus?.length > 0 ? (
            <TableContainer component={Paper} variant="outlined" sx={{ mt: 2, maxHeight: 280, overflow: 'auto' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Código</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Atributos</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Preço</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Sob demanda</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {confirmDialog.payload.affectedSkus.map((affected) => {
                    const fullSku = skus.find((s) => s.id === affected.id);
                    const attrs = fullSku?.attributes;
                    const attrsStr = attrs && typeof attrs === 'object' && !Array.isArray(attrs)
                      ? Object.entries(attrs).map(([k, v]) => `${k}: ${v}`).join(', ')
                      : Array.isArray(attrs)
                        ? attrs.map((a) => `${a.key || a.attribute_key}: ${a.value || a.attribute_value}`).join(', ')
                        : '-';
                    const price = fullSku?.price != null ? formatCurrency(fullSku.price) : '-';
                    const isMTO = fullSku?.is_made_to_order ? 'Sim' : 'Não';
                    return (
                      <TableRow key={affected.id}>
                        <TableCell sx={{ fontFamily: 'monospace' }}>{affected.code}</TableCell>
                        <TableCell sx={{ fontSize: '0.85rem', maxWidth: 200 }}>{attrsStr}</TableCell>
                        <TableCell>{price}</TableCell>
                        <TableCell>{isMTO}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          ) : null
        }
        onConfirm={handleConfirmDelete}
        onCancel={handleConfirmCancel}
        confirmText={confirmDialog.type === 'variation_attribute' ? 'Continuar' : 'Excluir'}
        cancelText="Cancelar"
        confirmColor={confirmDialog.type === 'variation_attribute' ? 'warning' : 'error'}
      />

      <SavingOverlay open={blockingOverlayOpen} />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ProductFormPage;
